// src/components/Sidebar.tsx
import React from 'react';
import {
  Box,
  Paper,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { groupByDate } from '../utils/groupByDate';

interface ChatItem {
  id: string;
  title: string;
  created: string; // Include 'created' date
}

interface SidebarProps {
  chats: ChatItem[];
  isFetchingChats: boolean;
  isCreatingChat: boolean;
  selectedChatId: string | null;
  handleCreateChat: () => void;
  handleSelectChat: (chatId: string) => void;
  isSendingMessage: boolean;
  isFetchingMessages: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  chats,
  isFetchingChats,
  isCreatingChat,
  selectedChatId,
  handleCreateChat,
  handleSelectChat,
  isSendingMessage,
  isFetchingMessages,
}) => {
  // Group chats by date
  const groupedChats = groupByDate(chats);

  // Sort dates in descending order (most recent first)
  const sortedDates = Object.keys(groupedChats).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <Paper sx={{ height: '85vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleCreateChat}
          disabled={isCreatingChat}
          startIcon={isCreatingChat && <CircularProgress size={20} color="inherit" />}
        >
          {isCreatingChat ? 'Creating...' : 'New Chat'}
        </Button>
      </Box>
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {isFetchingChats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          sortedDates.map((date) => (
            <React.Fragment key={date}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {date}
              </Typography>
              <Divider />
              {groupedChats[date].map((chat: ChatItem) => (
                <ListItemButton
                  key={chat.id}
                  selected={chat.id === selectedChatId}
                  onClick={() => handleSelectChat(chat.id)}
                  disabled={isSendingMessage || isFetchingMessages}
                >
                  <ListItemText primary={chat.title || 'A new chat...'} />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))
        )}
      </List>
    </Paper>
  );
};

export default Sidebar;
